/**
 * SlowRequestPrompt — shown next to a loading spinner when a fetch is taking
 * too long. After `thresholdMs` of continuous loading (default 8s) it surfaces a
 * "Taking too long? Let us know" link. Clicking opens a small dialog that lets
 * the user file a support ticket pre-filled with diagnostics about the slow
 * request: when it started, how many seconds elapsed by the time Submit is
 * pressed, which screen/loan it was on, the URL, the signed-in user, the partner
 * build, and the browser/viewport.
 *
 * Self-timing: the start of the request is taken as this component's mount time
 * (it's rendered only while the loader is up, i.e. while the request is in
 * flight), so callers just render it inside their loading branch. Pass `startedAt`
 * (epoch ms) to override with a precise fetch-start timestamp when one is known.
 *
 * Files an "Issue Report" support ticket via supportTicketsApi.create — the same
 * pipe as the Support screen, so it lands in the helpdesk + Teams notice.
 */
import { useEffect, useMemo, useRef, useState } from "react";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import InputBase from "@mui/material/InputBase";
import AccessTimeOutlined from "@mui/icons-material/AccessTimeOutlined";
import { useSnackbar } from "notistack";

import { supportTicketsApi } from "../../api/supportTicketsApi";
import { useEnv } from "../../hooks/useEnv";
import { useReduxHook } from "../../hooks/useReduxHook";
import { T } from "../../screens/SupportScreen/components/supportUi";
import { getRecentConsoleLogs } from "../../utils/consoleCapture";

const escapeHtml = (s = "") =>
  String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const SlowRequestPrompt = ({
  context = "the portal",
  details = {},
  relatedDealId,
  thresholdMs = 8000,
  sx,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { user, userEmail } = useReduxHook();
  const { partnerName, displayName } = useEnv();

  // Request start = mount time (the loader, and therefore this component, is
  // only rendered while the request is in flight). Stable across re-renders.
  const startedAtRef = useRef(Date.now());

  const [show, setShow] = useState(false);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Live "elapsed since the request started", refreshed once a second so the
  // dialog shows the real wait time at the moment Submit is pressed.
  const [nowTs, setNowTs] = useState(Date.now());

  useEffect(() => {
    const timer = setTimeout(() => setShow(true), thresholdMs);
    return () => clearTimeout(timer);
  }, [thresholdMs]);

  useEffect(() => {
    if (!show) return undefined;
    const id = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [show]);

  const elapsedSeconds = Math.max(
    0,
    Math.round((nowTs - startedAtRef.current) / 1000),
  );

  const diagnostics = useMemo(() => {
    const startedAt = startedAtRef.current;
    return {
      Screen: context,
      "Request started": new Date(startedAt).toISOString(),
      "Started (local)": new Date(startedAt).toLocaleString(),
      ...details,
      URL: typeof window !== "undefined" ? window.location.href : "",
      User: userEmail || user?.fullName || "(unknown)",
      Partner: partnerName || displayName || "",
      Browser:
        typeof navigator !== "undefined" ? navigator.userAgent : "(unknown)",
      Viewport:
        typeof window !== "undefined"
          ? `${window.innerWidth}×${window.innerHeight}`
          : "",
    };
    // details is an object literal from the caller; intentionally not deep-compared
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context, JSON.stringify(details), userEmail, user, partnerName, displayName]);

  const submit = async () => {
    setError(null);
    setBusy(true);
    // Snapshot elapsed at the instant Submit is pressed.
    const waitedSeconds = Math.max(
      0,
      Math.round((Date.now() - startedAtRef.current) / 1000),
    );
    const rows = {
      ...diagnostics,
      "Elapsed before submit": `${waitedSeconds} seconds`,
    };
    const detailRows = Object.entries(rows)
      .map(
        ([k, v]) =>
          `<li><strong>${escapeHtml(k)}:</strong> ${escapeHtml(v)}</li>`,
      )
      .join("");
    const userNote = message.trim()
      ? `<p><strong>What the user reported:</strong> ${escapeHtml(
          message.trim(),
        )}</p>`
      : "";

    // Recent console output captured app-wide (see utils/consoleCapture) — the
    // most useful signal for "it just hung". Empty if nothing was logged.
    const recentLogs = getRecentConsoleLogs(40);
    const consoleBlock = recentLogs.length
      ? `<p><strong>Recent console output (last ${recentLogs.length})</strong></p>` +
        `<pre style="white-space:pre-wrap;font-size:12px">${recentLogs
          .map(escapeHtml)
          .join("\n")}</pre>`
      : "";

    const description =
      `<p>Auto-reported from the "Taking too long?" slow-load prompt — a request on ` +
      `${escapeHtml(context)} was still loading after ${Math.round(
        thresholdMs / 1000,
      )}s.</p>` +
      userNote +
      `<p><strong>Diagnostics</strong></p><ul>${detailRows}</ul>` +
      consoleBlock;

    try {
      await supportTicketsApi.create({
        subject: `Slow load (${waitedSeconds}s) — ${context}`,
        description,
        category: "Issue Report",
        severity: "Medium",
        relatedDealId: relatedDealId || undefined,
      });
      enqueueSnackbar(
        "Thanks — we've been notified about the slow load.",
        { variant: "success" },
      );
      setOpen(false);
      setMessage("");
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Couldn't send the report. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  };

  if (!show) return null;

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          fontSize: 13,
          color: T.ink500,
          ...sx,
        }}
      >
        <span>Taking too long?</span>
        <Box
          component="button"
          type="button"
          onClick={() => setOpen(true)}
          sx={{
            font: "inherit",
            fontWeight: 600,
            color: T.teal600,
            background: "none",
            border: "none",
            p: 0,
            cursor: "pointer",
            textDecoration: "underline",
            textUnderlineOffset: "2px",
          }}
        >
          Let us know
        </Box>
      </Box>

      <Dialog
        open={open}
        onClose={() => (busy ? null : setOpen(false))}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: T.radius } }}
      >
        <Box sx={{ p: "22px 24px 16px" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              mb: "6px",
            }}
          >
            <AccessTimeOutlined sx={{ fontSize: 20, color: T.teal600 }} />
            <Box sx={{ fontSize: 15, fontWeight: 700, color: T.ink900 }}>
              Report a slow load
            </Box>
          </Box>
          <Box sx={{ fontSize: 13, color: T.ink500, lineHeight: 1.5 }}>
            This request to load {context} has been running for{" "}
            <strong>{elapsedSeconds} seconds</strong>. Submitting sends our team
            a support ticket with the details below so we can look into it.
          </Box>
        </Box>

        <Box sx={{ px: "24px", pb: "8px" }}>
          <Box
            sx={{
              fontSize: 12.5,
              fontWeight: 600,
              color: T.ink700,
              mb: "7px",
            }}
          >
            Anything you want to add?
            <Box
              component="span"
              sx={{ color: T.ink300, fontWeight: 500, ml: "4px" }}
            >
              · optional
            </Box>
          </Box>
          <InputBase
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            multiline
            minRows={2}
            maxRows={5}
            placeholder="e.g. it spun and never finished, or this is the third time today…"
            sx={{
              width: "100%",
              px: "12px",
              py: "9px",
              fontSize: 14,
              border: `1px solid ${T.line}`,
              borderRadius: "8px",
              background: "#fff",
              "&.Mui-focused": { borderColor: T.teal500 },
            }}
          />

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            p: "16px 24px",
            mt: 1,
            borderTop: `1px solid ${T.line}`,
            background: "#fbfdfe",
          }}
        >
          <Button
            variant="contained"
            onClick={submit}
            disabled={busy}
            startIcon={
              busy ? <CircularProgress size={15} color="inherit" /> : null
            }
            sx={{
              background: T.teal600,
              "&:hover": { background: "#127f97" },
              borderRadius: "8px",
            }}
          >
            {busy ? "Submitting…" : "Submit"}
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => setOpen(false)}
            disabled={busy}
            sx={{ borderColor: T.line, color: T.ink700, borderRadius: "8px" }}
          >
            Cancel
          </Button>
          <Box sx={{ ml: "auto", fontSize: 12, color: T.ink500 }}>
            🎫 Sent to support on submit
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default SlowRequestPrompt;
